'use client';

import type { ReactNode } from 'react';

type SettingsParamRowSpacing = 'param' | 'row';

const SPACINGS: Record<SettingsParamRowSpacing, string> = {
  param: 'py-space-6',
  row: 'py-space-16',
};

type SettingsParamRowProps = {
  /** Parameter label — the prototype's `.plabel`. */
  label: ReactNode;
  /** Supporting copy under the label. */
  description?: ReactNode;
  /** The control(s) for this parameter. */
  children?: ReactNode;
  /**
   * Stacks the control under the label across the full width, for controls that
   * need the room (textareas, template editors) — the prototype's `.prow.block`.
   */
  block?: boolean;
  /** Bottom hairline; the prototype leaves the last row of a panel open. */
  divider?: boolean;
  /**
   * Vertical rhythm. `param` is the tight panel spacing a revealed parameter
   * uses; `row` matches `SettingsRow`, for a nested row that is a setting in its
   * own right and sits in the same list as the capability above it — which is
   * how the prototype draws its indented Automations rows.
   */
  spacing?: SettingsParamRowSpacing;
  /**
   * Full-width content under both columns — the nested equivalent of
   * `SettingsRow`'s `footer`. A nested row that is a real setting rather than a
   * static parameter needs somewhere to put its own secondary UI (a change-reason
   * panel, a details disclosure), and the two-column line has no room for it.
   */
  footer?: ReactNode;
  /** Associates the label with its control; renders the label as a <label>. */
  htmlFor?: string;
  className?: string;
};

/**
 * A dependent parameter, indented under the capability it belongs to — the
 * prototype's `.srow.sub`.
 *
 * THE INDENT MUST NOT MOVE THE CONTROL COLUMN. The prototype achieves that by
 * keeping the lead column a fixed 440px and putting the indent INSIDE it
 * (`.srow.sub .srow-left { padding-left: 24px }`), so `.srow-value` starts at
 * 504px on a nested row exactly as it does on a parent.
 *
 * This component indents the row instead and narrows the lead column by the same
 * amount, which lands in the same place:
 *
 *     nested   24px indent + 26rem (416px) lead + 64px gutter = 504px
 *     parent                 27.5rem (440px) lead + 64px gutter = 504px
 *
 * Change the parent's basis in SettingsRow and this constant has to move with
 * it, or a nested control floats past the control it belongs to.
 */
export const SettingsParamRow = ({
  label,
  description,
  children,
  block = false,
  divider = true,
  spacing = 'param',
  footer,
  htmlFor,
  className,
}: SettingsParamRowProps) => {
  const LabelTag = htmlFor ? 'label' : 'span';

  return (
    <div
      data-lula-ds=""
      // Column wrapper, mirroring SettingsRow: the two-column line is its own
      // element and the footer sits BELOW it in normal flow. The footer used to
      // be a `basis-full` item inside the two-column flex container, which made
      // it a wrapped flex line — and with `items-center` the row then reserved
      // cross-axis space it did not need, leaving a tall empty band between a
      // nested control and its disclosure.
      className={`flex flex-col ${SPACINGS[spacing]} pl-space-24 font-sans${
        divider ? ' border-b border-solid border-border-subtle last:border-b-0' : ''
      }${className ? ` ${className}` : ''}`}
    >
      <div
        className={`flex flex-wrap${
          block ? ' flex-col gap-space-8' : ' items-center gap-space-16 md:gap-space-64'
        }`}
      >
        <div
          className={`flex min-w-0 flex-col gap-space-4${
            block ? '' : ' shrink grow-0 basis-full pr-space-24 md:basis-[26rem] md:max-w-[26rem]'
          }`}
        >
          <LabelTag
            htmlFor={htmlFor}
            className="typography-label-md-strong m-0 min-w-0 text-text-primary"
          >
            {label}
          </LabelTag>
          {description && (
            <span className="typography-body-xs m-0 text-text-tertiary">{description}</span>
          )}
        </div>
        {children && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-space-8">{children}</div>
        )}
      </div>
      {footer && <div className="mt-space-12 min-w-0">{footer}</div>}
    </div>
  );
};

export default SettingsParamRow;
